import { ConfigProvider, Layout, notification, theme } from 'antd';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { PropsWithChildren, useEffect } from 'react';
import VersionInfo from '../../components/VersionInfo';
import useActiveTicketChangeListener from '../../components/plugins/useActiveTicketChangeListener';
import SideMenu from '../../containers/MainLayout/SideMenu';
import TabViewContainer from '../../containers/MainLayout/TabViewContainer';
import useActiveChatEntities from '../../hooks/plugins/chat/useActiveChatEntities';
import useChatConnectionState from '../../hooks/plugins/chat/useChatConnectionState';
import useCurrentTabData from '../../hooks/useCurrentTabData';
import { usePluginStore, useTabStore, useUtilStore } from '../../store/RootStoreContext';
import { MAIN_SIDEBAR_WIDTH, MIN_WIDTH } from '../../styles/theme';
import { isNewTicketPage } from '../../utils/routeHelper';
import { getPluginStore } from '../../utils/store/getStore';

const { Sider, Content } = Layout;

notification.config({
    placement: 'bottomLeft'
});

function AppInitializer() {
    const { data } = useSession();
    const { loadPluginDefinitions } = usePluginStore();
    // Fetch tenant's plugins
    useEffect(() => {
        const loadAllPlugins = async () => {
            try {
                await loadPluginDefinitions(data);
            } catch (error) {
                console.error('MainLayout error on loadAllPlugins', error);
                // notification.error({ message: 'Cannot connect app right now' });
                return;
            }
        };

        loadAllPlugins();
    });

    return null;
}

function UtilityController() {
    const { events } = useRouter();
    const { setLastVisitedFilterPageUrl } = useUtilStore();

    useActiveTicketChangeListener();

    // handle store by reacting routing-through-nextjs
    useEffect(() => {
        const handleRouteChangeStart = (_url: string) => {
            return;
        };
        const handleRouteChangeEnd = (url: any) => {
            if (typeof url === 'string' && url.startsWith('/filters/')) {
                setLastVisitedFilterPageUrl(url);
            }
        };

        events.on('routeChangeStart', handleRouteChangeStart);
        events.on('routeChangeComplete', handleRouteChangeEnd);
        events.on('routeChangeError', handleRouteChangeEnd);
        return () => {
            events.off('routeChangeStart', handleRouteChangeStart);
            events.off('routeChangeComplete', handleRouteChangeEnd);
            events.off('routeChangeError', handleRouteChangeEnd);
        };
    }, [events, setLastVisitedFilterPageUrl]);

    return null;
}

function TabsController() {
    const { asPath } = useRouter();
    const { updateTicketTabs } = useTabStore();

    const initialTabDataSource = useCurrentTabData();

    useEffect(() => {
        updateTicketTabs(initialTabDataSource, true);
    }, [initialTabDataSource, updateTicketTabs]);

    useEffect(() => {
        isNewTicketPage(asPath) && updateTicketTabs(undefined, true);
    }, [asPath, updateTicketTabs]);

    return null;
}
function SubscribeChatController() {
    const { connectedToChat } = useChatConnectionState();

    const { data } = useActiveChatEntities({ requestFlag: connectedToChat });
    useEffect(() => {
        connectedToChat && data && getPluginStore().subscribeActiveChatEntities(data);
    }, [connectedToChat, data]);

    return null;
}

const styles = { root: { minWidth: MIN_WIDTH } };

export default function MainLayout({ children }: PropsWithChildren) {
    const {
        token: { colorPrimary, colorWhite }
    } = theme.useToken();

    return (
        <>
            <AppInitializer />
            <UtilityController />
            <SubscribeChatController />
            <TabsController />
            <Layout className="full-height" hasSider style={styles.root}>
                <ConfigProvider
                    theme={{
                        token: {
                            colorTextBase: colorWhite,
                            colorBgBase: colorPrimary
                        },
                        components: {
                            Tooltip: {
                                colorBgDefault: colorPrimary
                            }
                        }
                    }}
                >
                    <Sider collapsedWidth={MAIN_SIDEBAR_WIDTH} collapsed theme="light">
                        <SideMenu />
                        <VersionInfo />
                    </Sider>
                </ConfigProvider>
                <Content>
                    <TabViewContainer>
                        <ConfigProvider
                            theme={{
                                components: {
                                    Layout: {
                                        // sider component
                                        // colorBgContainer: colorBgContainer
                                    }
                                }
                            }}
                        >
                            {children}
                        </ConfigProvider>
                    </TabViewContainer>
                </Content>
            </Layout>
        </>
    );
}
